import React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { CssBaseline, Box, useMediaQuery } from '@mui/material';
import MuiAppBar from '@mui/material/AppBar';
import LeftDrawer from './LeftDrawer';
import DrawerHeader from './DrawerHeader';
import { Outlet } from 'react-router-dom';

import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';

import MenuOpenIcon from '@mui/icons-material/MenuOpen';

import NotiContainer from '../../../containers/common/header/NotiContainer';
import UserMenuContainer from '../../../containers/common/header/UserMenuContainer';
import LinkBoxContainer from '../../../containers/common/header/LinkBoxContainer';
import ProjectCounterContainer from '../../../containers/common/projectCounter/ProjectCounterContainer';

const drawerWidth = 240;

const Main = styled('main', {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'isMobile',
})(
  ({ theme, open, isMobile }) => ({
    flexGrow: 1,
    minWidth: 0,
    height: '100dvh',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    padding: isMobile ? theme.spacing(1.5) : theme.spacing(3),
    boxSizing: 'border-box',
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: isMobile ? 0 : `-${drawerWidth}px`,
    ...(open && !isMobile && {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
  }),
);

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'isMobile',
})(({ theme, open, isMobile }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && !isMobile && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerTemplate = (props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // isMobile(md, 900px)은 드로어 종류(temporary/persistent)를 결정하는 용도로 그대로 둔다.
  // 헤더 쪽은 그보다 더 넓은 범위에서 비좁다: 왼쪽 드로어가 기본으로 펼쳐져 있으면(>=900px)
  // AppBar 실제 가용폭이 "뷰포트 - 240px"로 줄어드는데, "할 일" 칩 5개(프로젝트/유지보수/수금/
  // 작업 지원 요청/작업 지원 예정) + 알림종·사용자메뉴·업무용 사이트 링크를 모두 라벨로
  // 펼쳐두면 약 1024px가 필요하다 - 900~1199px(md~lg 구간)에서는 이 가용폭을 넘어서 사용자
  // 메뉴/링크 박스가 화면 오른쪽 밖으로 밀려났다(2026-10-22, 1024px 실측: 가용폭 784px인데
  // 필요폭 ~950px, 사용자 메뉴 블록이 뷰포트 밖으로 173px 밀려났었다). 이 구간에서는 칩을
  // 모바일과 같은 압축 모드(아이콘+숫자, 눌러서 드로어)로 접고 업무용 사이트 링크도 숨긴다.
  const isCompactHeader = useMediaQuery(theme.breakpoints.down('lg'));
  const [open, setOpen] = React.useState(!isMobile);

  React.useEffect(() => {
    setOpen(!isMobile);
  }, [isMobile]);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  // 메뉴 검색 단축키(Ctrl+K / macOS ⌘K) 는 어디서나 눌릴 수 있어야 한다.
  // MenuList 는 모바일에서 드로어가 닫혀 있으면 DOM 에 아예 없으므로(아래 조건부 렌더),
  // 리스너는 항상 떠 있는 이 컴포넌트가 들고 있다가: (1) 드로어를 열고 (2) 토큰을 올려
  // MenuList 쪽에 "포커스 해라" 신호만 내려보낸다(모바일이면 그 사이에 새로 마운트된다).
  const [focusSearchToken, setFocusSearchToken] = React.useState(0);

  React.useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      const key = (e.key || '').toLowerCase();
      if (key === 'k' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        setOpen(true);
        setFocusSearchToken((t) => t + 1);
      }
    };
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh' }}>
      <CssBaseline />

      <AppBar position="fixed" open={open} isMobile={isMobile} color="default">
        <Toolbar
          variant="dense"
          sx={{
            minHeight: { xs: 48, sm: 56 },
            px: { xs: 1, sm: 2 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
            }}
          >
            <Box>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                onClick={handleDrawerOpen}
                edge="start"
                sx={{
                  mr: { xs: 0.5, md: 2 },
                  ...(open && !isMobile && { display: 'none' }),
                }}
              >
                <MenuOpenIcon />
              </IconButton>
            </Box>
          </Box>
          <Box sx={{ mx: { xs: 0, md: 1 } }}>
            <NotiContainer />
          </Box>
          <Box>
            <ProjectCounterContainer compact={isCompactHeader} />
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.5, md: 1 },
              pl: { xs: 0, md: 1 },
              py: { xs: 0, md: 1 },
              '& > *': {
                marginRight: { md: 1 },
              },
            }}
          >
            <UserMenuContainer />
            {!isCompactHeader && <LinkBoxContainer />}
          </Box>
        </Toolbar>
      </AppBar>

      {(!isMobile || open) && (
        <LeftDrawer
          theme={theme}
          drawerWidth={drawerWidth}
          open={open}
          handleDrawerClose={handleDrawerClose}
          variant={isMobile ? 'temporary' : 'persistent'}
          onMenuSelect={isMobile ? handleDrawerClose : undefined}
          focusSearchToken={focusSearchToken}
        />
      )}

      <Main open={open} isMobile={isMobile}>
        <DrawerHeader />
        {/* 셸(헤더·사이드바)은 화면에 고정하고 본문만 스크롤한다.
            Main이 height:100dvh + overflow:hidden 이라 여기서 스크롤 영역을 잡아주지 않으면
            내용이 긴 화면(등록 폼 등)이 잘린 채 스크롤되지 않는다. */}
        <Box
          sx={{
            flexGrow: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            overflowX: 'hidden',
            '&::-webkit-scrollbar': { width: '8px' },
            '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '4px' },
            '&::-webkit-scrollbar-thumb': {
              background: '#c1c1c1',
              borderRadius: '4px',
              '&:hover': { background: '#a8a8a8' },
            },
          }}
        >
          <Outlet />
        </Box>
      </Main>
    </Box>
  );
};

export default DrawerTemplate;
